import { createReducer } from '@reduxjs/toolkit';

import { acceptPrivacyPolicy, acceptTermAndService, fetchSession, resetSystemScope } from './actions';
import { SystemState } from './types';

export const INITIAL_SYSTEM_STATE: SystemState = {
  session: {
    user: null,
    subscription: null,
    company: null,
    timezone: null,
    roles: [],
    branches: [],
    platformSettingsMap: {},
    companySettingsMap: {},
    featureFlagsMap: {},
    privacyPolicy: null,
    termsAndConditions: null,
    termsAndConditionsAccepted: [],
    privacyPolicyAccepted: [],
    timezoneId: null,
    environmentsObjList: [],
    pendingDrnPurchasedHitsCount: 0,
  },
  loadingAcceptTermOrPrivacyPolicy: false,
  latestFetchSessionRequestId: null,
};

/**
 * T031 (FR-042) — sequence-number guard against stale scope publications.
 *
 * `fetchSession` (company/branch/permission scope revalidation) can be dispatched from more than
 * one place (e.g. `AuthRoute`'s boundary-transition revalidation and `SessionRouter`'s pre-existing
 * token-keyed refetch), so two overlapping calls can resolve out of order. `createAsyncThunk`
 * already assigns a unique `requestId` to every dispatch and stamps it onto that dispatch's
 * `pending`/`fulfilled`/`rejected` actions (`action.meta.requestId`) — reused here as the sequence
 * number instead of introducing a second, competing counter. This mirrors `auth-controller.ts`'s
 * `LatestGenerationEpisode` pattern (start a new "latest" marker, reject anything that isn't it),
 * adapted to a plain `createReducer` shape rather than that class's Redux-slice coupling, since
 * `system/redux` has no `beginX`/generation-bearing action of its own to hang a matching guard off.
 * Kept in `state.latestFetchSessionRequestId` rather than module scope, so the reducer stays a pure
 * function of `(state, action)` — a module-scope variable would leak across every store instance
 * built from this reducer (e.g. multiple stores in the same test file) instead of being scoped to
 * the one store it actually guards.
 *
 * `pending` dispatches synchronously (before the thunk's async work starts), so the LAST `pending`
 * seen here is always the latest in-flight request regardless of how the underlying network calls
 * settle. `fulfilled` is only applied when its `requestId` still matches that latest value — an
 * older request that resolves after a newer one has started (and possibly already applied its own,
 * fresher result) can never overwrite it with stale company/branch/permission scope.
 */
export const systemReducer = createReducer(INITIAL_SYSTEM_STATE, (builder) => {
  builder
    .addCase(fetchSession.pending, (state, action) => {
      return {
        ...state,
        latestFetchSessionRequestId: action.meta.requestId,
      };
    })
    .addCase(fetchSession.rejected, (state) => state)
    .addCase(fetchSession.fulfilled, (state, action) => {
      if (action.meta.requestId !== state.latestFetchSessionRequestId) {
        // Superseded by a newer scope revalidation started after this one — never publish a
        // stale company/branch/permission scope over a newer (possibly already-applied) result.
        return state;
      }
      return {
        ...state,
        ...action.payload,
      };
    })
    .addCase(resetSystemScope, (state) => {
      return {
        ...state,
        session: INITIAL_SYSTEM_STATE.session,
        // A `fetchSession` still in flight belongs to the previous identity; clearing the marker makes
        // the `fulfilled` guard above drop it.
        latestFetchSessionRequestId: null,
      };
    })
    .addCase(acceptTermAndService.pending, (state) => {
      return {
        ...state,
        loadingAcceptTermOrPrivacyPolicy: true,
      };
    })
    .addCase(acceptTermAndService.fulfilled, (state, action) => {
      return {
        ...state,
        session: {
          ...state.session,
          termsAndConditionsAccepted: [
            ...state.session.termsAndConditionsAccepted,
            {
              termAndConditionId: action.payload.termAndConditionId,
              companyId: state?.session?.company?.id as number,
            },
          ],
        },
        loadingAcceptTermOrPrivacyPolicy: false,
      };
    })
    .addCase(acceptPrivacyPolicy.fulfilled, (state, action) => {
      return {
        ...state,
        session: {
          ...state.session,
          privacyPolicyAccepted: [
            ...state.session.privacyPolicyAccepted,
            {
              privacyPolicyId: action.payload.privacyPolicyId,
              companyId: state?.session?.company?.id as number,
            },
          ],
        },
        loadingAcceptTermOrPrivacyPolicy: false,
      };
    })
    .addDefaultCase((state) => state);
});
